/**
 * @file planningControl.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-23
 * @description One planner at a time on a planning board — the browser's half
 *              (the rules are the server's: utils/planningControl.js and
 *              services/planningControlService.js in the parent repo).
 *
 *              - PLANNING_TAB_ID: this page's own id. Control belongs to a tab,
 *                so it is made fresh on every page load (a duplicated tab or a
 *                reload is a new tab, and a reload of the planner's own tab
 *                takes control straight back — same user).
 *              - setActivePlanningBoard: while a board is open, every request
 *                to /api/transport/ carries X-Planning-Board and
 *                X-Planning-Tab (utils/readOnlyFetch adds them), so the server
 *                can refuse a write from a tab that does not hold the board.
 *              - The words: why the board is view only, and the notices.
 *              Pure apart from the module's own board/tab.
 *
 *              1.1.0 (2026-10-23, Cam): control is per board DAY — one user
 *              may plan different days in different tabs, and another user
 *              another day at the same time. setActivePlanningBoard takes the
 *              day too, so requests carry X-Planning-Date; the words name the
 *              day ("Amy Hill is planning the bulk board for Fri 23 Oct — view
 *              only"). dayLabel is the server's (utils/planningControl).
 */

const makeId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* fall through */ }
  return `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
};

export const PLANNING_TAB_ID = makeId();
export const PLANNING_CONTROL_EVENT = 'excelAgri:planning-control';

let activeBoard = null;
let activeDate = null;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** The board open in this tab ('bulk' | 'bag') and the day it shows, or null. */
export function setActivePlanningBoard(board, date = null) {
  activeBoard = board || null;
  activeDate = board && DATE_RE.test(String(date || '')) ? String(date) : null;
}
export function activePlanningBoard() { return activeBoard; }
export function activePlanningDate() { return activeDate; }

/** Headers for a request to `pathname` while a board is open (else none). */
export function planningHeaders(pathname) {
  if (!activeBoard || !String(pathname || '').startsWith('/api/transport/')) return null;
  const h = { 'X-Planning-Board': activeBoard, 'X-Planning-Tab': PLANNING_TAB_ID };
  if (activeDate) h['X-Planning-Date'] = activeDate;
  return h;
}

export const BOARD_NAMES = { bulk: 'the bulk board', bag: 'the bag board' };

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Fri 23 Oct" for a 'YYYY-MM-DD' day (the date is the day — no clock involved). */
export function dayLabel(date) {
  const s = String(date || '').slice(0, 10);
  if (!DATE_RE.test(s)) return '';
  const x = new Date(`${s}T12:00:00Z`);
  if (Number.isNaN(x.getTime())) return '';
  return `${WEEKDAYS[x.getUTCDay()]} ${x.getUTCDate()} ${MONTHS[x.getUTCMonth()]}`;
}

/** "the bulk board for Fri 23 Oct" once control is per day; "the bulk board" before. */
export function boardDayName(board, view) {
  const where = BOARD_NAMES[board] || 'this board';
  const day = view && view.perDay && view.date ? dayLabel(view.date) : '';
  return day ? `${where} for ${day}` : where;
}

const hhmm = (ms) => {
  if (!ms) return '';
  const d = new Date(Number(ms));
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Whole seconds left of an answer window, counting down from when the view arrived. */
export function secondsLeft(msLeft, receivedAt, now = Date.now()) {
  if (msLeft == null) return null;
  return Math.max(0, Math.ceil((Number(msLeft) - (now - Number(receivedAt || now))) / 1000));
}

/**
 * Why this tab may not change the board now, or null when it may (or when
 * there is no control yet — before the migration, or before the first answer).
 */
export function controlBlockReason(view, board) {
  if (!view || !view.ready) return null;
  const where = boardDayName(board, view);
  if (view.pending) return `Checking who is planning ${where}…`;
  if (view.role === 'holder') return null;
  const h = view.holder;
  if (view.role === 'requester') return `Waiting for ${h ? h.name : 'the planner'} to answer — view only until then`;
  if (!h) return `Nobody is planning ${where} — take control to make changes`;
  if (h.yourOtherTab) return `You are planning ${where} in another tab or window — this one is view only`;
  return `${h.name} is planning ${where} — view only`;
}

/**
 * The line shown at the top of the board for this tab: { tone, text, action }
 * where action is 'request' | 'take' | null. Null when there is nothing to say
 * (this tab is planning, or there is no control).
 */
export function controlNotice(view, board, { canPlan = true } = {}) {
  if (!view || !view.ready || view.role === 'holder' || view.pending) return null;
  const where = boardDayName(board, view);
  const h = view.holder;
  const n = view.notice;
  if (view.role === 'requester') {
    return { tone: 'info', text: `Access requested from ${h ? h.name : 'the planner'} — waiting for a response`, action: null, waiting: true };
  }
  if (n && n.kind === 'lost') {
    const at = hhmm(n.at);
    const text = n.how === 'timeout' ? `${n.byName} took over planning ${where} at ${at} because you didn’t answer. This screen is view only now.`
      : n.how === 'handed' ? `You handed ${where} to ${n.byName} at ${at}. This screen is view only now.`
      : n.how === 'same_user' ? `You opened ${where} in another tab or window at ${at} — this one is view only now.`
      : n.how === 'gone' ? `${n.byName} took over planning ${where} at ${at} — this screen had stopped responding. It is view only now.`
      : `${n.byName} is planning ${where} now (since ${at}). This screen is view only.`;
    return { tone: n.how === 'timeout' || n.how === 'gone' ? 'warn' : 'info', text, action: canPlan ? (n.how === 'same_user' ? 'take' : 'request') : null };
  }
  if (n && n.kind === 'busy') {
    return { tone: 'warn', text: `${n.byName} is planning ${where} and said it’s busy. You can view it; ask again later.`, action: canPlan ? 'request-again' : null };
  }
  if (!h) return { tone: 'info', text: `Nobody is planning ${where}.`, action: canPlan ? 'take' : null };
  if (h.yourOtherTab) return { tone: 'info', text: `You are planning ${where} in another tab or window — this one is view only.`, action: canPlan ? 'take' : null };
  const waiting = view.waiting ? ` ${view.waiting.name} has already asked for it.` : '';
  const stale = h.stale ? ' Their screen has stopped responding.' : '';
  return { tone: 'info', text: `${h.name} is planning ${where} — view only.${stale}${waiting}`, action: canPlan && !view.waiting ? 'request' : null };
}
